import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Grid, Loader, Input, Select, Icon, Popup } from "semantic-ui-react";
import FetchAPI from "../../api/FetchAPI";
import { getCurrentProjectID, toastError } from "../../app_shared_functions";
import { KeyPairsProps, KeyPairResponse, KeyPair } from "./types";
import { State } from "../../selectors/state";

const normalizeKeyType = (publicKey: string): string => {
  const rawType = publicKey.split(" ")[0].toLowerCase();
  if (rawType.includes("rsa")) return "rsa";
  if (rawType.includes("ed25519")) return "ed25519";
  if (rawType.includes("ecdsa")) return "ecdsa";
  return rawType;
};

const KeyPairs: React.FC<KeyPairsProps> = ({
  region,
  inputKey,
  handleChange,
  isRequired = false,
  allowedKeyTypes,
  description,
}) => {
  const [keyPairs, setKeyPairs] = useState<KeyPair[] | null>(null);

  const projects = useSelector((state: State) => state.projects);

  useEffect(() => {
    const project_id = getCurrentProjectID(projects, region);
    FetchAPI.Compute.KeyPairs.getList({ region, project_id })
      .then((response) => {
        if (response?.status === 200) {
          const data = response.data as KeyPairResponse[];
          const allKeyPairs = data.map((kp) => kp.keypair);
          const filteredKeyPairs = allowedKeyTypes
            ? allKeyPairs.filter((kp) =>
                allowedKeyTypes
                  .map((t) => t.toLowerCase())
                  .includes(normalizeKeyType(kp.public_key)),
              )
            : allKeyPairs;
          setKeyPairs(filteredKeyPairs);
          handleChange({ name: inputKey, value: "" });
        }
      })
      .catch((err) => {
        setKeyPairs([]);
        toastError(err, "Keypair list load failed");
      });
  }, [region, projects]);

  const dropdownOptions = useMemo(() => {
    return (
      keyPairs?.map((x, id) => ({
        key: id,
        text: x.name,
        value: x.name,
      })) || []
    );
  }, [keyPairs]);

  const handleDropdownChange = useCallback(
    (e: React.SyntheticEvent<HTMLElement>, data: any) => {
      handleChange({ name: inputKey, value: data.value });
    },
    [handleChange, inputKey],
  );

  const renderLoader = useCallback(
    () => (
      <Grid.Row>
        <Grid.Column textAlign="left" width={16}>
          <div className="loader-wrapper">
            <Loader size="mini" active className="one-liner">
              Fetching keypairs list...
            </Loader>
          </div>
        </Grid.Column>
      </Grid.Row>
    ),
    [],
  );
  const renderDropdown = useCallback(
    () => (
      <Grid.Row>
        <Grid.Column className="flex vcenter" textAlign="left" width={8}>
          <h5>
            Keypair
            {description && (
              <Popup
                content={description}
                trigger={
                  <Icon
                    name="info circle"
                    size="small"
                    style={{ marginLeft: 8, cursor: "pointer" }}
                  />
                }
              />
            )}
            {isRequired && (
              <span className="color-red padding-left-half bold">*</span>
            )}
          </h5>
        </Grid.Column>
        <Grid.Column className="flex vcenter" textAlign="left" width={8}>
          {keyPairs && keyPairs.length ? (
            <Select
              icon="chevron circle down"
              className="select-box full"
              placeholder="Choose KeyPair"
              options={dropdownOptions}
              onChange={handleDropdownChange}
            />
          ) : (
            <Input disabled value="No Keypairs found" />
          )}
        </Grid.Column>
      </Grid.Row>
    ),
    [keyPairs, dropdownOptions, handleDropdownChange, isRequired, description],
  );

  return !keyPairs ? renderLoader() : renderDropdown();
};

export default KeyPairs;
